import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Alert,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { drawModelTownBoard } from './boardDrawing';
import {
  generateRoute,
  validatePosition,
  validateRoadType,
  isNearRoundaboutEntry,
  isInsideRoundabout,
  detectEntryDirection,
  detectEntryLane,
  detectRoundaboutExit,
  validateRoundaboutExit,
  getNumLanesForDirection,
  getRoundaboutErrorMessage,
  validateRoundaboutLanePosition,
  didCrossYellowLine,
  didCrossRoundaboutEntryLine
} from './routeLogic';
import GameControls from './GameControls';

const ModelTownCanvas = () => {
  const canvasRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [vehicle, setVehicle] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [route, setRoute] = useState(null);
  const [currentCheckpoint, setCurrentCheckpoint] = useState(0);
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'info' });
  const [canvasSize, setCanvasSize] = useState({ width: 1000, height: 600 });
  const [scale, setScale] = useState(1);
  const [errorPosition, setErrorPosition] = useState(null); // Position where error occurred
  const [isViolation, setIsViolation] = useState(false); // Red flash for violations
  const [showPath, setShowPath] = useState(false); // Toggle to show/hide checkpoint path

  // Roundabout tracking
  const [roundaboutEntry, setRoundaboutEntry] = useState(null); // { lane, direction, numLanes }
  const [isInRoundabout, setIsInRoundabout] = useState(false);
  const [hasExitedRoundabout, setHasExitedRoundabout] = useState(false);

  // Error modal
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: '',
    description: '',
    rule: '',
    reminder: ''
  });

  // Responsive canvas sizing - portrait for mobile, landscape for desktop
  useEffect(() => {
    const updateCanvasSize = () => {
      if (isMobile) {
        // MOBILE: Fit entire board in viewport (no scrolling)
        // Canvas is 1000x600, rotated 90deg to show as 600x1000 portrait
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const controlsSpace = 80;

        const availableWidth = viewportWidth;
        const availableHeight = viewportHeight - controlsSpace;

        // After rotation: 600 becomes width, 1000 becomes height
        // Scale to fit BOTH dimensions
        const scaleToFitWidth = availableWidth / 600;
        const scaleToFitHeight = availableHeight / 1000;
        const newScale = Math.min(scaleToFitWidth, scaleToFitHeight); // Fit both

        console.log('Mobile Canvas Sizing:', {
          viewport: `${viewportWidth}x${viewportHeight}`,
          available: `${availableWidth}x${availableHeight}`,
          canvasSize: '1000x600 (FULL BOARD)',
          scale: newScale,
          displayedAfterRotation: `${600 * newScale}w x ${1000 * newScale}h`,
          note: 'Entire board fits in viewport without scrolling'
        });

        // Canvas is ALWAYS 1000x600 (full board size)
        setCanvasSize({ width: 1000, height: 600 });
        setScale(newScale);
      } else {
        // For desktop, maintain landscape orientation at 1000x600
        const container = canvasRef.current?.parentElement;
        if (container) {
          const containerWidth = container.clientWidth;
          const maxWidth = Math.min(containerWidth - 40, 1000);
          const aspectRatio = 600 / 1000;
          const newWidth = maxWidth;
          const newHeight = newWidth * aspectRatio;
          const newScale = newWidth / 1000;

          setCanvasSize({ width: newWidth, height: newHeight });
          setScale(newScale);
        }
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [isMobile]);

  // Initialize route once on mount
  useEffect(() => {
    const newRoute = generateRoute();
    setRoute(newRoute);
    setVehicle({
      x: newRoute.start.x,
      y: newRoute.start.y,
      angle: newRoute.start.angle
    });
    setCurrentCheckpoint(0);
  }, []); // Empty dependency - run once

  // Draw canvas whenever relevant state changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !route || !vehicle) return;

    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);

    // Draw board - NO SCALING HERE
    // The board is designed for 1000x600
    // Canvas IS 1000x600, so draw directly
    drawModelTownBoard(ctx);

    // Draw vehicle at actual coordinates (no scale multiplication)
    drawVehicle(ctx, vehicle.x, vehicle.y, vehicle.angle);

    // Draw checkpoints based on showPath toggle
    if (showPath) {
      // Show all checkpoints (full path)
      route.checkpoints.forEach((checkpoint, index) => {
        const isCompleted = index < currentCheckpoint;
        const isCurrent = index === currentCheckpoint;
        drawCheckpoint(ctx, checkpoint.x, checkpoint.y, isCompleted, isCurrent);
      });
    } else {
      // Show only the final destination checkpoint (last checkpoint)
      const lastCheckpointIndex = route.checkpoints.length - 1;
      const lastCheckpoint = route.checkpoints[lastCheckpointIndex];
      const isCompleted = currentCheckpoint >= lastCheckpointIndex;
      const isCurrent = currentCheckpoint === lastCheckpointIndex;
      drawCheckpoint(ctx, lastCheckpoint.x, lastCheckpoint.y, isCompleted, isCurrent);
    }

    // Draw error X mark if there's an error
    if (errorPosition) {
      drawErrorMark(ctx, errorPosition.x, errorPosition.y);
    }
  }, [canvasSize, vehicle, route, currentCheckpoint, scale, errorPosition, showPath]);

  const drawVehicle = (ctx, x, y, angle) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    // Vehicle body (red car) - SMALLER: 20x12 instead of 30x16
    ctx.fillStyle = '#ff4444';
    ctx.fillRect(-10, -6, 20, 12);

    // Vehicle front (darker red)
    ctx.fillStyle = '#cc0000';
    ctx.fillRect(8, -6, 2, 12);

    // Windows (dark)
    ctx.fillStyle = '#333';
    ctx.fillRect(-5, -4, 8, 3);
    ctx.fillRect(-5, 1, 8, 3);

    // Headlights
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(9, -4, 1, 2);
    ctx.fillRect(9, 2, 1, 2);

    ctx.restore();
  };

  const drawCheckpoint = (ctx, x, y, completed, current) => {
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, 10, 0, Math.PI * 2);

    if (completed) {
      ctx.fillStyle = '#4caf50';
      ctx.fill();
      ctx.strokeStyle = '#2e7d32';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Checkmark
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - 4, y);
      ctx.lineTo(x - 1, y + 4);
      ctx.lineTo(x + 5, y - 4);
      ctx.stroke();
    } else if (current) {
      ctx.fillStyle = '#ffeb3b';
      ctx.fill();
      ctx.strokeStyle = '#f57f17';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Pulsing effect (optional enhancement)
      ctx.strokeStyle = 'rgba(245, 127, 23, 0.3)';
      ctx.lineWidth = 5;
      ctx.stroke();
    } else {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.fill();
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    ctx.restore();
  };

  const drawErrorMark = (ctx, x, y) => {
    ctx.save();

    // Red circle background
    ctx.fillStyle = '#ff0000';
    ctx.beginPath();
    ctx.arc(x, y, 12, 0, Math.PI * 2);
    ctx.fill();

    // White X mark
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';

    // Draw X
    ctx.beginPath();
    ctx.moveTo(x - 6, y - 6);
    ctx.lineTo(x + 6, y + 6);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x + 6, y - 6);
    ctx.lineTo(x - 6, y + 6);
    ctx.stroke();

    ctx.restore();
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Get position relative to canvas element
    let relX = clientX - rect.left;
    let relY = clientY - rect.top;

    // If on mobile (canvas is rotated 90deg), we need to transform coordinates
    if (isMobile) {
      // The canvas is rotated 90deg clockwise
      // So we need to rotate the coordinates back
      // Original canvas is 1000x600, displayed as 600x1000 after rotation
      const tempX = relX;
      const tempY = relY;

      // Reverse the rotation: rotate -90deg (or +270deg)
      // For a 90deg clockwise rotation: x' = y, y' = width - x
      // For reverse: x = height - y', y = x'
      relX = tempY;
      relY = rect.width - tempX;
    }

    // Convert to canvas coordinates (unscaled)
    const canvasX = relX / scale;
    const canvasY = relY / scale;

    return { x: canvasX, y: canvasY };
  };

  const handleMouseDown = (e) => {
    if (!vehicle || !route) return;
    e.preventDefault();

    const { x, y } = getCanvasCoordinates(e);
    const dx = x - vehicle.x;
    const dy = y - vehicle.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 30) {
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !route) return;
    e.preventDefault();

    const { x, y } = getCanvasCoordinates(e);

    // CHECK YELLOW LINE CROSSING: Did vehicle cross a continuous yellow line?
    if (vehicle) {
      const yellowLineCrossing = didCrossYellowLine(vehicle.x, vehicle.y, x, y);

      if (yellowLineCrossing.crossed) {
        // YELLOW LINE VIOLATION!
        setIsViolation(true);
        setTimeout(() => setIsViolation(false), 200);

        setIsDragging(false);
        setErrorPosition({ x, y });

        console.log(`⚠️ YELLOW LINE VIOLATION:`, {
          from: `(${Math.round(vehicle.x)}, ${Math.round(vehicle.y)})`,
          to: `(${Math.round(x)}, ${Math.round(y)})`,
          lineType: yellowLineCrossing.type,
          crossingPoint: `(${Math.round(yellowLineCrossing.position.x)}, ${Math.round(yellowLineCrossing.position.y)})`
        });

        setErrorModal({
          open: true,
          title: 'Continuous Yellow Line Crossing - VIOLATION',
          description: `You crossed a continuous (solid) yellow line on the ${yellowLineCrossing.type}. This is strictly prohibited.`,
          rule: 'Vehicles MUST NOT cross continuous yellow lines. Yellow lines separate opposing traffic directions. You may only cross BROKEN (dotted) yellow lines at intersections.',
          reminder: 'MTB Rule: Continuous yellow lines = NO CROSSING. Broken yellow lines at intersections = CROSSING ALLOWED.'
        });
        return;
      }

      // CHECK ROUNDABOUT ENTRY LINE CROSSING: Did vehicle cross a continuous white line at roundabout entry?
      const whiteLineCrossing = didCrossRoundaboutEntryLine(vehicle.x, vehicle.y, x, y);

      if (whiteLineCrossing.crossed) {
        // WHITE LINE VIOLATION!
        setIsViolation(true);
        setTimeout(() => setIsViolation(false), 200);

        setIsDragging(false);
        setErrorPosition({ x, y });

        console.log(`⚠️ ROUNDABOUT ENTRY LINE VIOLATION:`, {
          from: `(${Math.round(vehicle.x)}, ${Math.round(vehicle.y)})`,
          to: `(${Math.round(x)}, ${Math.round(y)})`,
          lineType: whiteLineCrossing.type,
          crossingPoint: `(${Math.round(whiteLineCrossing.position.x)}, ${Math.round(whiteLineCrossing.position.y)})`
        });

        setErrorModal({
          open: true,
          title: 'Continuous White Line Crossing - VIOLATION',
          description: `You crossed a continuous (solid) white line at the ${whiteLineCrossing.type}. Lane changing is not allowed at roundabout entries.`,
          rule: 'Vehicles MUST NOT cross continuous white lines. These solid white lines before roundabout entries indicate that you must stay in your lane and not change lanes.',
          reminder: 'MTB Rule: Choose your lane BEFORE the solid white lines. Once you reach the solid white lines approaching a roundabout, you CANNOT change lanes.'
        });
        return;
      }
    }

    // REAL-TIME VALIDATION: Check if vehicle is on valid road (not green area, not on lines)
    const isOnValidRoad = validateRoadType(x, y);

    if (!isOnValidRoad) {
      // RED FLASH VIOLATION!
      setIsViolation(true);
      setTimeout(() => setIsViolation(false), 200); // Flash for 200ms

      setIsDragging(false);
      setErrorPosition({ x, y });

      setErrorModal({
        open: true,
        title: 'Off-Road Violation',
        description: 'You went off the road or onto a green area.',
        rule: 'Vehicles must stay on paved roads at all times.',
        reminder: 'Do NOT go into green areas (off road) and do NOT cross continuous yellow/white lines.'
      });
      return;
    }

    // ROUNDABOUT TRACKING AND VALIDATION
    const nearEntry = isNearRoundaboutEntry(x, y);
    const insideRoundabout = isInsideRoundabout(x, y);

    // DEBUG: Log roundabout state
    const dxRoundabout = x - 214;
    const dyRoundabout = y - 212;
    const distFromCenter = Math.sqrt(dxRoundabout * dxRoundabout + dyRoundabout * dyRoundabout);

    if (distFromCenter < 130) { // Near roundabout area
      console.log(`📍 Position check:`, {
        pos: `(${Math.round(x)}, ${Math.round(y)})`,
        distFromCenter: Math.round(distFromCenter),
        nearEntry,
        insideRoundabout,
        hasRoundaboutEntry: !!roundaboutEntry,
        isInRoundaboutState: isInRoundabout
      });
    }

    // Detect entry into roundabout
    if (nearEntry && !roundaboutEntry && !isInRoundabout) {
      const direction = detectEntryDirection(x, y);
      const lane = detectEntryLane(x, y, direction);
      const numLanes = getNumLanesForDirection(direction);

      console.log(`🔍 Checking entry:`, { direction, lane, numLanes });

      if (lane) {
        console.log(`🚗 ROUNDABOUT ENTRY DETECTED:`, {
          position: `(${Math.round(x)}, ${Math.round(y)})`,
          direction,
          approachLane: `Lane ${lane} (${lane === 1 ? 'OUTERMOST, near kerb' : lane === numLanes ? 'INNERMOST, near green median - will travel AROUND island' : 'middle'})`,
          numLanes: `${numLanes} lanes per direction`,
          legalExits: numLanes === 3 ?
            (lane === 1 ? 'Exit 1 ONLY (immediate)' : lane === 2 ? 'Exit 2 ONLY (straight)' : 'Exit 3 or U-turn (270°-360° around island)') :
            (lane === 1 ? 'Exit 1 ONLY (immediate)' : lane === 2 ? 'Exit 2 ONLY' : lane === 3 ? 'Exit 3 ONLY' : 'Exit 4 or U-turn (270°-360° around island)'),
          rule: `Lane ${lane} → Exit ${lane}. ${lane === numLanes ? 'INNERMOST: Travels AROUND green island' : lane === 1 ? 'OUTERMOST: Exits immediately' : 'Middle lane'}`
        });
        setRoundaboutEntry({ lane, direction, numLanes });
      } else {
        console.warn(`⚠️ Near entry but lane detection failed!`, { x, y, direction });
      }
    }

    // Track if vehicle is inside roundabout
    if (insideRoundabout && !isInRoundabout) {
      console.log(`🔴 ENTERED ROUNDABOUT CIRCLE`);
      setIsInRoundabout(true);
      setHasExitedRoundabout(false);
    }

    // ALLOW FREE MOVEMENT INSIDE ROUNDABOUT
    // Players can change lanes/circles inside the roundabout
    // Only the EXIT taken matters for validation (based on entry lane)

    // Detect exit from roundabout
    if (!insideRoundabout && isInRoundabout && roundaboutEntry && !hasExitedRoundabout) {
      setHasExitedRoundabout(true);

      // Validate the exit
      const exitNumber = detectRoundaboutExit(x, y, roundaboutEntry.direction);

      console.log(`🚪 ROUNDABOUT EXIT DETECTED:`, {
        exitPosition: `(${Math.round(x)}, ${Math.round(y)})`,
        entryLane: roundaboutEntry.lane,
        entryDirection: roundaboutEntry.direction,
        exitNumber: exitNumber,
        exitName: exitNumber === 1 ? '1st exit (left)' : exitNumber === 2 ? '2nd exit (straight)' : exitNumber === 3 ? '3rd exit (right)' : '4th exit (U-turn)'
      });

      if (exitNumber) {
        const isValidExit = validateRoundaboutExit(
          roundaboutEntry.lane,
          exitNumber,
          roundaboutEntry.numLanes
        );

        console.log(`✅ EXIT VALIDATION:`, {
          entryLane: roundaboutEntry.lane,
          exitTaken: exitNumber,
          isValid: isValidExit,
          reason: !isValidExit ? `Lane ${roundaboutEntry.lane} cannot take exit ${exitNumber}` : 'Correct exit'
        });

        if (!isValidExit) {
          // WRONG EXIT VIOLATION!
          setIsViolation(true);
          setTimeout(() => setIsViolation(false), 200);

          setIsDragging(false);
          setErrorPosition({ x, y });

          const errorMsg = getRoundaboutErrorMessage(
            roundaboutEntry.lane,
            exitNumber,
            roundaboutEntry.numLanes
          );

          setErrorModal({
            open: true,
            ...errorMsg
          });

          return;
        } else {
          console.log(`✅ CORRECT EXIT! Lane ${roundaboutEntry.lane} successfully took exit ${exitNumber}`);
        }
      }

      // Reset roundabout tracking for next roundabout
      setIsInRoundabout(false);
      setRoundaboutEntry(null);
    }

    // Calculate angle based on movement direction
    const dx = x - vehicle.x;
    const dy = y - vehicle.y;
    const angle = Math.atan2(dy, dx);

    setVehicle({ x, y, angle });

    // Check if reached current checkpoint
    if (currentCheckpoint < route.checkpoints.length) {
      const checkpoint = route.checkpoints[currentCheckpoint];
      const checkDx = x - checkpoint.x;
      const checkDy = y - checkpoint.y;
      const distToCheckpoint = Math.sqrt(checkDx * checkDx + checkDy * checkDy);

      if (distToCheckpoint < 35) {
        // Validate if correct path
        const isValid = validatePosition(x, y, checkpoint, currentCheckpoint, route);

        if (isValid) {
          setCurrentCheckpoint(prev => prev + 1);

          // Check if route completed
          if (currentCheckpoint + 1 >= route.checkpoints.length) {
            setIsDragging(false);
            setFeedback({
              open: true,
              message: '🎉 Congratulations! You completed the route correctly! Excellent driving!',
              severity: 'success'
            });
            setTimeout(() => {
              resetGame();
            }, 3000);
          } else {
            setFeedback({
              open: true,
              message: `✓ Checkpoint ${currentCheckpoint + 1} reached! Continue to the next one.`,
              severity: 'success'
            });
          }
        } else {
          setIsDragging(false);
          setErrorPosition({ x, y }); // Show X mark at error position

          setErrorModal({
            open: true,
            title: 'Wrong Route - Traffic Rule Violation',
            description: 'You did not follow the correct route or violated traffic rules at this checkpoint.',
            rule: checkpoint.rule || 'Follow the designated route and observe all traffic rules.',
            reminder: 'Study the traffic rules carefully and try again.'
          });
        }
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetGame = () => {
    const newRoute = generateRoute();
    setRoute(newRoute);
    setVehicle({
      x: newRoute.start.x,
      y: newRoute.start.y,
      angle: newRoute.start.angle
    });
    setCurrentCheckpoint(0);
    setIsDragging(false);
    setErrorPosition(null); // Clear error mark
    setRoundaboutEntry(null); // Reset roundabout tracking
    setIsInRoundabout(false);
    setHasExitedRoundabout(false);
  };

  const handleCloseErrorModal = () => {
    setErrorModal({ ...errorModal, open: false });
    // Reset game after user closes modal
    setTimeout(() => {
      resetGame();
    }, 300);
  };

  const handleCloseFeedback = () => {
    setFeedback({ ...feedback, open: false });
  };

  if (!route || !vehicle) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading game...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      // RED FLASH OVERLAY FOR VIOLATIONS
      '&::before': isViolation ? {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 0, 0, 0.4)',
        pointerEvents: 'none',
        zIndex: 9999,
        animation: 'violationFlash 200ms ease-out'
      } : {},
      '@keyframes violationFlash': {
        '0%': { backgroundColor: 'rgba(255, 0, 0, 0.6)' },
        '100%': { backgroundColor: 'rgba(255, 0, 0, 0)' }
      }
    }}>
      {isMobile ? (
        // MOBILE: Fit entire board in viewport (no scrolling)
        <Box sx={{
          position: 'relative',
          width: '100vw',
          height: `calc(100vh - 80px)`,
          overflow: 'hidden', // NO SCROLLING - board fits viewport
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Box sx={{
            transform: 'rotate(90deg)',
            transformOrigin: 'center center',
            width: `${canvasSize.width * scale}px`,
            height: `${canvasSize.height * scale}px`
          }}>
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              style={{
                width: `${canvasSize.width * scale}px`,
                height: `${canvasSize.height * scale}px`,
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none',
                display: 'block'
              }}
            />
          </Box>
        </Box>
      ) : (
        // DESKTOP: Normal landscape orientation
        <Box sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '1000px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
            style={{
              border: '3px solid #333',
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none',
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
          />
        </Box>
      )}

      {/* Progress indicator - Compact for mobile */}
      <Box sx={{
        position: isMobile ? 'fixed' : 'relative',
        bottom: isMobile ? 0 : 'auto',
        left: isMobile ? 0 : 'auto',
        right: isMobile ? 0 : 'auto',
        mt: isMobile ? 0 : 2,
        mb: 0,
        width: isMobile ? '100%' : 'auto',
        bgcolor: isMobile ? 'rgba(26, 26, 26, 0.95)' : 'transparent',
        backdropFilter: isMobile ? 'blur(10px)' : 'none',
        borderTop: isMobile ? '1px solid #333' : 'none',
        py: isMobile ? 1 : 0,
        px: isMobile ? 2 : 0,
        zIndex: isMobile ? 1000 : 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        boxShadow: isMobile ? '0 -4px 20px rgba(0,0,0,0.5)' : 'none'
      }}>
        {/* Progress and Route Name */}
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          gap: isMobile ? 2 : 0.25,
          width: '100%',
          justifyContent: isMobile ? 'space-between' : 'center',
          alignItems: isMobile ? 'center' : 'flex-start'
        }}>
          <Typography
            variant="caption"
            sx={{
              color: '#4caf50',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              fontWeight: 'bold'
            }}
          >
            {currentCheckpoint}/{route.checkpoints.length} Checkpoints
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#999',
              fontSize: isMobile ? '0.65rem' : '0.75rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {route.name}
          </Typography>
        </Box>

        {/* Toggle Path Button */}
        <Tooltip title={showPath ? "Hide Path" : "Show Path"}>
          <IconButton
            onClick={() => setShowPath(!showPath)}
            sx={{
              bgcolor: showPath ? '#4caf50' : '#666',
              color: '#fff',
              '&:hover': {
                bgcolor: showPath ? '#45a049' : '#555'
              },
              width: isMobile ? 40 : 45,
              height: isMobile ? 40 : 45,
              mt: 1
            }}
          >
            {showPath ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Game Controls - Floating SpeedDial for mobile, buttons for desktop */}
      <GameControls onNewRoute={resetGame} />

      {/* Error Modal - Must be explicitly closed */}
      <Dialog
        open={errorModal.open}
        onClose={handleCloseErrorModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1a1a1a',
            color: '#fff',
            border: '2px solid #f44336'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: '#fff', fontWeight: 'bold' }}>
          {errorModal.title}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#fff' }}>
            {errorModal.description}
          </Typography>

          <Box sx={{ p: 2, bgcolor: '#2a2a2a', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#ff9800', fontWeight: 'bold', mb: 1 }}>
              Rule Violated:
            </Typography>
            <Typography variant="body2" sx={{ color: '#ddd' }}>
              {errorModal.rule}
            </Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: '#2a2a2a', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#4caf50', fontWeight: 'bold', mb: 1 }}>
              Reminder:
            </Typography>
            <Typography variant="body2" sx={{ color: '#ddd' }}>
              {errorModal.reminder}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseErrorModal}
            variant="contained"
            sx={{
              bgcolor: '#f44336',
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: '#d32f2f'
              }
            }}
          >
            Close & Try Again
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          sx={{ width: '100%', fontSize: isMobile ? '0.875rem' : '1rem' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModelTownCanvas;
